import type { Comment, Session } from "../types/core";

/**
 * Formats the session transcript and interactions into a chronological Markdown document.
 */
export function formatTranscript(session: Session): string {
	const transcript = session.transcript;
	const interactions = session.interactions || [];
	const comments = session.comments || [];
	const sessionStartTime = session.startTime.getTime();

	// Performance optimization: Use a Map for O(1) lookup of comments by ID.
	// This avoids an O(I * C) bottleneck where for each interaction,
	// we would scan the entire comments list.
	// Using a loop instead of .map() to avoid intermediate array allocation.
	const commentMap = new Map<string, Comment>();
	for (const c of comments) {
		commentMap.set(c.id, c);
	}

	// Pre-filter valid pin interactions and their content.
	// We only care about pins that have corresponding comments.
	// Performance optimization: Check if pins are already sorted during collection
	// to avoid O(N log N) sort in the common chronological case.
	const pins: { timestamp: number; content: string }[] = [];
	let pinsSorted = true;
	for (let i = 0; i < interactions.length; i++) {
		const interaction = interactions[i];
		if (interaction.type === "pin") {
			const comment = commentMap.get(interaction.commentId);
			if (comment) {
				const timestamp = interaction.timestamp.getTime();
				if (
					pinsSorted &&
					pins.length > 0 &&
					pins[pins.length - 1].timestamp > timestamp
				) {
					pinsSorted = false;
				}
				pins.push({
					timestamp,
					content: comment.content,
				});
			}
		}
	}

	// Only sort if we detected an out-of-order interaction.
	if (!pinsSorted) {
		pins.sort((a, b) => a.timestamp - b.timestamp);
	}

	// Performance optimization: Consolidate filtering and sorting check into a single O(N) pass.
	// This avoids multiple array allocations and redundant scans of the transcript.
	const finalSegments = [];
	let isSorted = true;
	for (let i = 0; i < transcript.length; i++) {
		const s = transcript[i];
		if (s.isFinal) {
			if (
				isSorted &&
				finalSegments.length > 0 &&
				finalSegments[finalSegments.length - 1].start > s.start
			) {
				isSorted = false;
			}
			finalSegments.push(s);
		}
	}

	// Only sort if the single-pass check found an out-of-order segment.
	// We sort the existing array in-place to avoid another allocation.
	if (!isSorted) {
		finalSegments.sort((a, b) => a.start - b.start);
	}
	const sortedFinalSegments = finalSegments;

	const outputParts: string[] = [];
	let tIdx = 0;
	let pIdx = 0;

	// Performance optimization: Merge two chronologically sorted streams in O(N+M) time.
	// This avoids O((N+M) log (N+M)) sorting of the entire combined list and reduces
	// memory pressure by eliminating intermediate event objects for every segment.
	while (tIdx < sortedFinalSegments.length || pIdx < pins.length) {
		const segment =
			tIdx < sortedFinalSegments.length ? sortedFinalSegments[tIdx] : null;
		const pin = pIdx < pins.length ? pins[pIdx] : null;

		if (
			segment &&
			(!pin || sessionStartTime + segment.start <= pin.timestamp)
		) {
			outputParts.push(segment.text);
			tIdx++;
		} else if (pin) {
			outputParts.push(`コメント：${pin.content}`);
			pIdx++;
		} else {
			break;
		}
	}

	return outputParts.join("\n\n").trim();
}
